/**
 * The inline panels under the EW invoice's band (no modals): edit a draft's
 * paperwork, mark it sent, record a payment, void it with a reason. Each one
 * is admin / office staff only — the record page draws them only for those
 * roles — and every refusal reads as a sentence through ewRefusal.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useMarkEwInvoiceSent, useRecordEwPayment, useUpdateEwInvoice, useVoidEwInvoice } from '../../../hooks/useEwInvoices';
import { blankToNull, ewRefusal, fmtMoney } from '../../../lib/earthworks';
import { EW_PAYMENT_METHODS } from '../../../lib/ew-invoices';
import type { EwInvoice, EwInvoiceDraftWrite, EwPaymentMethod } from '../../../types/ew-invoices';
import { EW_DATE_INPUT, EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError, EwPanel } from '../EwBits';

function PanelButtons({ busy, label, onCancel, onSubmit, disabled = false, testId }: {
  busy: boolean; label: string; onCancel: () => void; onSubmit: () => void; disabled?: boolean; testId: string;
}) {
  return (
    <div className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-4 py-3">
      <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={busy}>Cancel</button>
      <button type="button" className={EW_PRIMARY} onClick={onSubmit} disabled={busy || disabled} data-testid={testId}>
        {busy ? 'Saving…' : label}
      </button>
    </div>
  );
}

export function EwInvoiceEditPanel({ inv, onDone }: { inv: EwInvoice; onDone: () => void }) {
  const update = useUpdateEwInvoice();
  const [dueDate, setDueDate] = useState(inv.dueDate ?? '');
  const [po, setPo] = useState(inv.poNumber ?? '');
  const [afe, setAfe] = useState(inv.afeNumber ?? '');
  const [cost, setCost] = useState(inv.costCode ?? '');
  const [notes, setNotes] = useState(inv.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    setError(null);
    const body: EwInvoiceDraftWrite = { po_number: blankToNull(po), afe_number: blankToNull(afe), cost_code: blankToNull(cost), notes: blankToNull(notes) };
    if (dueDate) body.due_date = dueDate;
    update.mutate({ id: inv.id, body }, {
      onSuccess: () => { toast.success('Invoice saved'); onDone(); },
      onError: (err) => setError(ewRefusal(err, 'The invoice could not be saved.')),
    });
  };

  return (
    <EwPanel eyebrow="Draft" title="Edit details" testId="ew-invoice-edit-panel">
      <div className="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <EwField label="Due date">
          <input type="date" className={EW_DATE_INPUT} aria-label="Due date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </EwField>
        <EwField label="PO number">
          <input className={EW_INPUT} aria-label="PO number" maxLength={100} value={po} onChange={(e) => setPo(e.target.value)} />
        </EwField>
        <EwField label="AFE number">
          <input className={EW_INPUT} aria-label="AFE number" maxLength={100} value={afe} onChange={(e) => setAfe(e.target.value)} />
        </EwField>
        <EwField label="Cost code">
          <input className={EW_INPUT} aria-label="Cost code" maxLength={100} value={cost} onChange={(e) => setCost(e.target.value)} />
        </EwField>
        <div className="sm:col-span-2 lg:col-span-4">
          <EwField label="Notes">
            <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </EwField>
        </div>
        <p className="text-xs text-fieldiq-muted sm:col-span-2 lg:col-span-4">A draft's paperwork only. Its lines and money are fixed when it is made.</p>
        <div className="sm:col-span-2 lg:col-span-4"><EwFormError message={error} /></div>
      </div>
      <PanelButtons busy={update.isPending} label="Save" onCancel={onDone} onSubmit={save} testId="ew-invoice-edit-save" />
    </EwPanel>
  );
}

export function EwInvoiceMarkSentPanel({ inv, onDone }: { inv: EwInvoice; onDone: () => void }) {
  const markSent = useMarkEwInvoiceSent();
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    markSent.mutate(inv.id, {
      onSuccess: () => { toast.success(`${inv.invoiceNumber} marked sent`); onDone(); },
      onError: (err) => setError(ewRefusal(err, 'The invoice could not be marked sent.')),
    });
  };
  return (
    <EwPanel eyebrow="Send" title="Mark sent" testId="ew-invoice-mark-sent-panel">
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        <p>
          Records that the office sent {inv.invoiceNumber} to the customer itself. No email goes from here. Once sent, the
          invoice can take payments and its details can no longer be edited.
        </p>
        <EwFormError message={error} />
      </div>
      <PanelButtons busy={markSent.isPending} label="Mark sent" onCancel={onDone} onSubmit={go} testId="ew-invoice-mark-sent-confirm" />
    </EwPanel>
  );
}

export function EwInvoicePaymentPanel({ inv, today, onDone }: { inv: EwInvoice; today: string | null; onDone: () => void }) {
  const record = useRecordEwPayment();
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(today ?? '');
  const [method, setMethod] = useState<string>('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const go = () => {
    setError(null);
    const n = Number(amount.trim());
    if (amount.trim() === '' || !Number.isFinite(n) || n <= 0) return setError('Enter the amount received.');
    if (!paidOn) return setError('Enter the day it was received.');
    if (!method) return setError('Pick how it was paid.');
    record.mutate({
      id: inv.id,
      body: {
        amount: n, paid_on: paidOn, method: method as EwPaymentMethod,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
    }, {
      onSuccess: () => { toast.success('Payment recorded'); onDone(); },
      onError: (err) => setError(ewRefusal(err, 'The payment could not be recorded.')),
    });
  };

  return (
    <EwPanel eyebrow="Payment" title="Record payment" testId="ew-invoice-payment-panel">
      <div className="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <EwField label="Amount" hint={`Still owing: ${fmtMoney(inv.balanceDue, inv.currency)}`}>
          <input className={EW_INPUT} aria-label="Payment amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </EwField>
        <EwField label="Received on">
          <input type="date" className={EW_DATE_INPUT} aria-label="Payment received on" max={today ?? undefined} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </EwField>
        <EwField label="Method">
          <select className={EW_INPUT} aria-label="Payment method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Pick…</option>
            {EW_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </EwField>
        <EwField label="Reference (optional)">
          <input className={EW_INPUT} aria-label="Payment reference" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
        </EwField>
        <div className="sm:col-span-2 lg:col-span-4">
          <EwField label="Notes (optional)">
            <input className={EW_INPUT} aria-label="Payment notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </EwField>
        </div>
        <div className="sm:col-span-2 lg:col-span-4"><EwFormError message={error} /></div>
      </div>
      <PanelButtons busy={record.isPending} label="Record payment" onCancel={onDone} onSubmit={go} testId="ew-invoice-payment-save" />
    </EwPanel>
  );
}

export function EwInvoiceVoidPanel({ inv, onDone }: { inv: EwInvoice; onDone: () => void }) {
  const voidInv = useVoidEwInvoice();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    if (!reason.trim()) return setError('Say why the invoice is being voided.');
    voidInv.mutate({ id: inv.id, reason: reason.trim() }, {
      onSuccess: () => { toast.success(`${inv.invoiceNumber} voided`); onDone(); },
      onError: (err) => setError(ewRefusal(err, 'The invoice could not be voided.')),
    });
  };
  return (
    <EwPanel eyebrow="Void" title={`Void ${inv.invoiceNumber}`} testId="ew-invoice-void-panel">
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        <p>Voiding is final. The field tickets go back to waiting to be invoiced, and a deposit credited here goes back to its quote.</p>
        <EwField label="Reason">
          <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Void reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </EwField>
        <EwFormError message={error} />
      </div>
      <PanelButtons busy={voidInv.isPending} label="Void invoice" onCancel={onDone} onSubmit={go} disabled={!reason.trim()} testId="ew-invoice-void-confirm" />
    </EwPanel>
  );
}

export type EwInvoicePanel = 'edit' | 'sent' | 'payment' | 'void' | null;
